/**
 * Edit this clip — the four INTENT sections (captions & text, sound, camera &
 * framing, re-cut from scratch) plus the recovery block for failed /
 * incomplete jobs. Moved out of JobDetail (spec 2026-09-16 §2.4): the video
 * page keeps the whole panel only as "Recover this clip" for jobs without a
 * video; ready jobs edit inside the Studio's Edit bar, which mounts ONE
 * section at a time through `only`.
 *
 * Every action keeps its server route. What changed is only where the
 * outcome lands: a host that passes `onJobStarted` follows the job itself
 * (the Studio polls it exactly like Approve & render; the page re-reads the
 * job so its progress list appears). Without it the page-era behaviour
 * stands — a reload for the remix / restore paths, which have no live job to
 * subscribe to.
 */
import {
  AlertTriangle,
  Camera,
  Crosshair,
  Languages,
  Loader2,
  MessageSquareQuote,
  Pencil,
  RotateCcw,
  Undo2,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  changeMusicTrack,
  getMusicSettings,
  listMusicTracks,
  listRenders,
  polishCamera,
  reclip,
  regenerateCaption,
  regenerateMusicVolume,
  regenerateSubtitles,
  restoreRender,
  retryJob,
  shiftCaptions,
  type JobStatus,
  type MusicSettings,
  type MusicTrack,
} from "../../api";
import { useIsMobile } from "../../lib/useIsMobile";
import { MusicPicker } from "../MusicPicker";
import ReframePanel from "../ReframePanel";
import { ctaGhostStyle, ctaSmallStyle, fieldLabelStyle, inputStyle, monoInputStyle } from "../styles";
import { CostBadge, EDIT_CARD_GRID, EditSectionRow } from "./EditSectionRow";
import { MusicTrackCard, MusicVolumeCard } from "./MusicCards";

export type EditClipSectionId = "captions" | "sound" | "camera" | "recut";

type EndMode = "end" | "duration";

/** "1:30" or "90" → 90 seconds. NaN if unparseable. */
function parseDurationInput(s: string): number {
  const t = s.trim();
  if (!t) return NaN;
  if (/^\d+$/.test(t)) return Number(t);
  const parts = t.split(":").map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return NaN;
  if (parts.length === 2) return parts[0]! * 60 + parts[1]!;
  if (parts.length === 3) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  return NaN;
}

export interface EditPanelProps {
  jobId: string;
  status: JobStatus;
  hasVideo: boolean;
  hasTranscript: boolean;
  originalFrom?: string;
  originalTo?: string;
  originalDuration?: number;
  originalPhrase?: string;
  originalEndPhrase?: string;
  /** Music filename from the prior submission (so the re-cut picker
   *  pre-populates with what the operator picked last time). Empty /
   *  undefined → start the picker at the automatic pick. */
  originalMusicTrack?: string;
  /**
   * Render ONE section's cards without the panel's own chrome (no
   * collapsible header, no section rows, no recovery block) — the host names
   * the group. Absent ⇒ the whole panel, as the video page mounts it.
   */
  only?: EditClipSectionId;
  /**
   * A server-side job (render or remix) started, or a restore landed. The
   * host follows it; without this the panel keeps the page-era behaviour
   * (a reload where a live job has nothing to subscribe to).
   */
  onJobStarted?: () => void;
}

export function EditClipPanel({
  jobId,
  status,
  hasVideo,
  hasTranscript,
  originalFrom,
  originalTo,
  originalDuration,
  originalPhrase,
  originalEndPhrase,
  originalMusicTrack,
  only,
  onJobStarted,
}: EditPanelProps): React.JSX.Element {
  const isMobile = useIsMobile();
  // Recovery jobs auto-open with the Re-cut card focused: failed, incomplete,
  // and a job that says ready but has no video file behind it (the mp4 was
  // deleted / never landed) — same dead end, same way out (round 1, item 2).
  const isRecovery = status === "incomplete" || status === "failed" || !hasVideo;
  // A no-re-entry retry reuses work/<clip>/input.json. We can offer it iff the
  // original start exists (`originalFrom` = input.from) — uploads have no
  // url/from, so they fall through to the manual re-cut form instead.
  const canReuse = Boolean(originalFrom);
  const [open, setOpen] = useState(isRecovery);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Most recent archived render that still has a video file, for the one-click
  // "Restore previous render" recovery action. null = nothing to restore.
  const [restorableIdx, setRestorableIdx] = useState<number | null>(null);

  const [newFrom, setNewFrom] = useState(originalFrom ?? "");
  const [newPhrase, setNewPhrase] = useState(originalPhrase ?? "");
  const [newEndPhrase, setNewEndPhrase] = useState(originalEndPhrase ?? "");
  // Mode mirrors what the original submission used: if it had a
  // duration, default to Duration mode; otherwise End-timestamp mode.
  // Pre-fill both inputs so flipping the toggle keeps the user's data.
  const [endMode, setEndMode] = useState<EndMode>(
    originalDuration !== undefined ? "duration" : "end",
  );
  const [newTo, setNewTo] = useState(originalTo ?? "");
  const [newDuration, setNewDuration] = useState(
    originalDuration !== undefined ? String(originalDuration) : "",
  );
  // Music — initialised from the original pick so a "submit the
  // re-cut as-is" inherits it explicitly. The picker treats "" as
  // the automatic pick; we send the operator's choice verbatim to the
  // server (which accepts a tri-state filename / "" / undefined) so
  // intent is unambiguous on the wire.
  const [musicTracks, setMusicTracks] = useState<MusicTrack[] | null>(null);
  const [musicTrack, setMusicTrack] = useState<string>(originalMusicTrack ?? "");
  const [musicError, setMusicError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMusicTracks()
      .then((tracks) => { if (!cancelled) setMusicTracks(tracks); })
      .catch((err) => {
        if (cancelled) return;
        setMusicError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, []);

  // On a recovery job, look for an archived render to offer "Restore previous
  // render" — e.g. an interrupted fine-pan re-render moved the prior mp4 to
  // out/archives/<clip>/ before the restart, so the last good version is still
  // there for an instant rollback.
  useEffect(() => {
    if (!isRecovery) return;
    let cancelled = false;
    listRenders(jobId)
      .then((items) => {
        if (cancelled) return;
        const latest = items.reduce<number | null>(
          (best, it) => (it.hasVideo && (best === null || it.idx > best) ? it.idx : best),
          null,
        );
        setRestorableIdx(latest);
      })
      .catch(() => { if (!cancelled) setRestorableIdx(null); });
    return () => { cancelled = true; };
  }, [jobId, isRecovery]);

  const handleShift = async (offsetMs: number): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await shiftCaptions(jobId, offsetMs);
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
    // On success, status flips to rendering and the panel hides.
  };

  // Camera-only fix: fresh framing/pan/opening-shot judgments + re-render,
  // captions and music reused byte-identical. On success the status flips to
  // rendering and the live subscription takes over — like handleReclip, we
  // only clear `busy` on error.
  // Reframe opens a modal rather than firing a job: the operator aims first,
  // and the re-render is the LAST step (the panel calls it itself).
  const [reframeOpen, setReframeOpen] = useState(false);

  /** Swap the music bed on a finished render — ffmpeg-only remix, no
   *  re-render. Before this the only way to change the track was a full
   *  re-cut, even though the fast path already existed server-side. */
  const handleChangeMusicTrack = async (): Promise<void> => {
    if (busy || !musicTrack.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await changeMusicTrack(jobId, musicTrack);
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const handlePolishCamera = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await polishCamera(jobId);
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const handleReclip = async (opts: { finePan?: boolean } = {}): Promise<void> => {
    if (busy || !newFrom.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const durationSec = endMode === "duration" ? parseDurationInput(newDuration) : NaN;
      if (endMode === "duration" && newDuration.trim() && !Number.isFinite(durationSec)) {
        throw new Error('Duration must be "90" (seconds) or "1:30" (mm:ss).');
      }
      await reclip(jobId, {
        from: newFrom.trim(),
        to: endMode === "end" ? (newTo.trim() || undefined) : undefined,
        duration: endMode === "duration" && Number.isFinite(durationSec) ? durationSec : undefined,
        startPhrase: newPhrase.trim() || undefined,
        endPhrase: newEndPhrase.trim() || undefined,
        finePan: opts.finePan === true ? true : undefined,
        // Always send the operator's current pick verbatim — empty
        // string maps to explicit Auto on the server, a filename to a
        // switch, and the form was pre-populated with the prior pick
        // so leaving it untouched re-uses it.
        musicTrack,
      });
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  // One-click recovery: re-run the pipeline with the EXACT parameters this clip
  // was created with (start, end/duration, phrases, fine-pan, music) read from
  // the persisted input.json — zero re-entry. On success the status flips to
  // "rendering" and the page's live subscription unmounts this panel, so (like
  // handleReclip) we only clear `busy` on error.
  const handleRetryReuse = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await retryJob(jobId);
      onJobStarted?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        msg.includes("no input.json")
          ? "Original parameters weren't saved for this job — use Re-cut below to set a new start."
          : `Retry failed: ${msg}`,
      );
      setBusy(false);
    }
  };

  // Instant rollback: copy the most recent archived render back over canonical.
  // No re-render — the job becomes "ready" again. No live job spawns, so the
  // page reloads to re-read the job from disk (mirrors the render-history
  // Restore button); a following host re-reads it itself.
  const handleRestoreLatest = async (): Promise<void> => {
    if (busy || restorableIdx === null) return;
    setBusy(true);
    setError(null);
    try {
      await restoreRender(jobId, restorableIdx);
      if (onJobStarted) onJobStarted();
      else window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const handleRegenerateCaption = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await regenerateCaption(jobId);
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const handleRegenerateSubtitles = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await regenerateSubtitles(jobId);
      onJobStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  // Music-volume slider state. Loaded from the server (reads music.json's
  // stored volume — defaults to 0.14 for clips that pre-date this feature).
  // `applied` tracks what was last sent so the Apply button can disable
  // until the slider actually changes.
  const [musicSettings, setMusicSettings] = useState<MusicSettings | null>(null);
  const [musicVolume, setMusicVolume] = useState<number>(0.14);
  const [musicApplied, setMusicApplied] = useState<number>(0.14);

  useEffect(() => {
    if (!hasVideo) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await getMusicSettings(jobId);
        if (cancelled) return;
        setMusicSettings(s);
        setMusicVolume(s.volume);
        setMusicApplied(s.volume);
      } catch {
        /* non-fatal — the slider falls back to the 0.14 default already set */
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, hasVideo]);

  const handleApplyMusicVolume = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await regenerateMusicVolume(jobId, musicVolume);
      // The remix is now a live job, but a ready page has no SSE
      // subscription — without a reload the whole panel stays disabled
      // forever and the ~10s remix completes invisibly (same pattern as
      // handleShift / Restore). A following host polls it instead.
      if (onJobStarted) onJobStarted();
      else window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const errorRow = error && (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        background: "rgba(248,113,113,.06)",
        border: "1px solid rgba(248,113,113,.30)",
        borderRadius: 10,
        padding: "9px 12px",
        color: "var(--ql-danger)",
        fontSize: 12,
        lineHeight: 1.5,
        marginBottom: 12,
        fontFamily: "var(--ql-font-mono)",
      }}
    >
      <AlertTriangle size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1.5 }} />
      <span>{error}</span>
    </div>
  );

  /*
    Sections are grouped by INTENT — what is wrong with the video —
    not by cost, which is how they were grouped until 2026-08-24:

      Captions & text   — the words are wrong or mistimed
      Sound             — the bed is too loud, or the wrong track
      Camera & framing   — the crop is aimed badly
      Re-cut from scratch — the wrong seconds entirely

    Cost belongs on each BUTTON (CostBadge), not on the section:
    the operator picks by symptom, then sees what the fix costs.
    Grouping by duration meant knowing that "Reframe" was a
    re-render before you could find it.

    All four start COLLAPSED (operator ask) — the section list is
    the menu; unfolding one is choosing a subject. Each keeps a
    faint background wash, and cards inside flow in a 2-col
    auto-fit grid (single column below ~580px viewport).

    Each section's cards are built once here so the panel can render them
    inside its own rows (the page) or hand ONE section to a host (`only`).
  */

  const captionsCards = (
    <>
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          background: "rgba(255,255,255,.02)",
          border: "1px solid rgba(255,255,255,.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
            }}
          >
            Caption timing
          </span>
          <CostBadge>~30s</CostBadge>
        </div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12 }}>
          Shift every caption a little earlier or later. Rebuilds the video without re-cutting it.
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[-1000, -500, -250, 250, 500, 1000].map((ms) => (
            <button
              key={ms}
              onClick={() => handleShift(ms)}
              disabled={busy}
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                fontSize: 12,
                fontFamily: "var(--ql-font-mono)",
                fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer",
                background: ms < 0 ? "rgba(96,165,250,.10)" : "rgba(255,213,0,.10)",
                border: ms < 0 ? "1px solid rgba(96,165,250,.30)" : "1px solid rgba(255,213,0,.30)",
                color: ms < 0 ? "#60A5FA" : "var(--ql-yellow)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              {ms > 0 ? `+${ms}` : ms}ms
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 10 }}>
          Negative = captions appear earlier. Positive = later.
        </div>
      </div>

      {hasVideo && hasTranscript && (
        <div
          style={{
            padding: 16,
            borderRadius: 14,
            background: "rgba(255,255,255,.02)",
            border: "1px solid rgba(255,255,255,.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ql-yellow)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Captions
            </span>
            <CostBadge>~30s–2min</CostBadge>
          </div>
          <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12, flex: 1 }}>
            Write the captions again from the transcript and rebuild the video. The previous version stays under Versions.
          </div>
          <button
            onClick={handleRegenerateSubtitles}
            disabled={busy}
            style={{
              ...ctaGhostStyle,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? <Loader2 size={14} className="ql-spin" /> : <Languages size={14} strokeWidth={2} />}
            Regenerate captions
          </button>
        </div>
      )}

      {hasTranscript && (
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          background: "rgba(255,255,255,.02)",
          border: "1px solid rgba(255,255,255,.06)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
            }}
          >
            Post caption
          </span>
          <CostBadge>~20s</CostBadge>
        </div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12, flex: 1 }}>
          Write the post caption again. Stays under Instagram's 2200-character cap.
        </div>
        <button
          onClick={handleRegenerateCaption}
          disabled={busy}
          style={{
            ...ctaGhostStyle,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
        >
          {busy ? <Loader2 size={14} className="ql-spin" /> : <MessageSquareQuote size={14} strokeWidth={2} />}
          Regenerate caption
        </button>
      </div>
      )}
    </>
  );

  const soundCards = (
    <>
      <MusicVolumeCard
        value={musicVolume}
        onChange={setMusicVolume}
        applied={musicApplied}
        defaultValue={musicSettings?.default ?? 0.14}
        min={musicSettings?.min ?? 0}
        max={musicSettings?.max ?? 0.5}
        onApply={handleApplyMusicVolume}
        busy={busy}
      />

      <MusicTrackCard
        tracks={musicTracks}
        selected={musicTrack}
        onChange={setMusicTrack}
        onTracksChange={setMusicTracks}
        loadError={musicError}
        onApply={handleChangeMusicTrack}
        busy={busy}
      />
    </>
  );

  const cameraCards = (
    <>
      {/* Reframe — the AIMED counterpart to the polish below. Same
          source requirement (an upload has no re-croppable source),
          but the operator says WHERE and WHAT instead of re-rolling
          the whole clip's framing blind. */}
      {hasVideo && canReuse && (
        <div
          style={{
            background: "rgba(255,255,255,.02)",
            border: "1px solid rgba(255,255,255,.07)",
            borderRadius: 14,
            padding: 16,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ql-yellow)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Reframe
            </span>
            <CostBadge>~2–4 min</CostBadge>
          </div>
          <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12, flex: 1 }}>
            You know exactly which shot is wrong? Aim the crop box yourself on the source
            frame, or freeze the speaker over real B-roll. Only the shots you touch change.
          </div>
          <button
            onClick={() => setReframeOpen(true)}
            disabled={busy}
            style={{
              ...ctaGhostStyle,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            <Crosshair size={14} strokeWidth={2} />
            Reframe shots
          </button>
        </div>
      )}

      {/* Camera polish — the "a scene looks bad but everything else is
          right" fix. Re-judges framing, pans and the opening shot from
          scratch (fine-pan sampling) and re-renders; captions, timing
          and music are reused byte-identical. Uploads can't re-crop
          (no source), hence the canReuse gate. */}
      {hasVideo && canReuse && (
        <div
          style={{
            padding: 16,
            borderRadius: 14,
            background: "rgba(255,255,255,.02)",
            border: "1px solid rgba(255,255,255,.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ql-yellow)",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Polish camera
            </span>
            <CostBadge>~3–6 min</CostBadge>
          </div>
          <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 12, flex: 1 }}>
            Bad opening scene or the crop cuts the speaker? Re-judges framing, pans and the
            opening shot from scratch and re-renders. Captions, timing and music stay exactly
            as they are.
          </div>
          <button
            onClick={handlePolishCamera}
            disabled={busy}
            style={{
              ...ctaGhostStyle,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? <Loader2 size={14} className="ql-spin" /> : <Camera size={14} strokeWidth={2} />}
            Polish camera
          </button>
        </div>
      )}

      {reframeOpen && (
        <ReframePanel
          jobId={jobId}
          onClose={() => setReframeOpen(false)}
          onApplied={() => { setBusy(true); onJobStarted?.(); }}
        />
      )}
    </>
  );

  const recutCards = (
    <div
      style={{
        padding: 16,
        borderRadius: 14,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Re-cut from a new start
        </span>
        <CostBadge>~5–10min</CostBadge>
      </div>
      <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 6 }}>
        Starts over from the source and builds the whole video again. Use this when the clip starts on the wrong sentence.
      </div>
      {hasVideo && (
        <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginBottom: 12 }}>
          Trimming a few seconds? Use Start &amp; end instead — this form re-picks the moment from scratch.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <div style={fieldLabelStyle}>Approx. start</div>
          <input
            value={newFrom}
            onChange={(e) => setNewFrom(e.target.value)}
            placeholder="8:23"
            style={monoInputStyle}
          />
        </div>
        <div>
          <div
            style={{
              ...fieldLabelStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span>
              {endMode === "end" ? "End" : "Duration"}{" "}
              <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>(optional)</span>
            </span>
            <ReclipEndModeToggle mode={endMode} onChange={setEndMode} />
          </div>
          {endMode === "end" ? (
            <input
              key="reclip-end"
              value={newTo}
              onChange={(e) => setNewTo(e.target.value)}
              placeholder="auto"
              style={monoInputStyle}
            />
          ) : (
            <input
              key="reclip-duration"
              value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              placeholder="1:30 or 90"
              style={monoInputStyle}
            />
          )}
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={fieldLabelStyle}>First spoken words (optional)</div>
        <input
          value={newPhrase}
          onChange={(e) => setNewPhrase(e.target.value)}
          placeholder='e.g. "If you are awake"'
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={fieldLabelStyle}>Last spoken words (optional)</div>
        <input
          value={newEndPhrase}
          onChange={(e) => setNewEndPhrase(e.target.value)}
          placeholder='e.g. "to themselves"'
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...fieldLabelStyle, marginBottom: 8 }}>Music for the new cut</div>
        <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginBottom: 8 }}>
          Only applies to this re-cut. To change the bed on the current
          render without re-cutting, use Change music under Sound.
        </div>
        <MusicPicker
          tracks={musicTracks}
          selected={musicTrack}
          onChange={setMusicTrack}
          onTracksChange={setMusicTracks}
          loadError={musicError}
        />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={() => handleReclip()}
          disabled={busy || !newFrom.trim()}
          style={{
            ...ctaSmallStyle,
            cursor: busy || !newFrom.trim() ? "not-allowed" : "pointer",
            opacity: busy || !newFrom.trim() ? 0.5 : 1,
          }}
        >
          {busy ? <Loader2 size={14} className="ql-spin" /> : <RotateCcw size={14} strokeWidth={2} />}
          Build it again
        </button>
        <button
          onClick={() => handleReclip({ finePan: true })}
          disabled={busy || !newFrom.trim()}
          title="Builds the WHOLE video again with denser camera-pan sampling — captions get re-written too. For a camera-only fix that keeps captions exactly as they are, use Polish camera (or Reframe, to aim it yourself) under Camera & framing."
          style={{
            ...ctaGhostStyle,
            cursor: busy || !newFrom.trim() ? "not-allowed" : "pointer",
            opacity: busy || !newFrom.trim() ? 0.5 : 1,
          }}
        >
          {busy ? <Loader2 size={14} className="ql-spin" /> : <RotateCcw size={14} strokeWidth={2} />}
          Build again · Fine pan
        </button>
      </div>
    </div>
  );

  // ---- ONE section for a host that names the group itself ----
  if (only) {
    const available =
      only === "recut" ? true
      : only === "camera" ? hasVideo && canReuse
      : hasVideo;
    const body =
      only === "captions" ? captionsCards
      : only === "sound" ? soundCards
      : only === "camera" ? cameraCards
      : recutCards;
    return (
      <div>
        {errorRow}
        {available ? (
          <div style={EDIT_CARD_GRID}>{body}</div>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.55, padding: "6px 2px" }}>
            {!hasVideo
              ? "There is no video to edit yet — build one first."
              : "This video was uploaded, so there is no source footage to re-crop. Re-cut from scratch still works."}
          </div>
        )}
      </div>
    );
  }

  // ---- The whole panel (the video page's recovery mode) ----
  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 18,
        marginBottom: 20,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 18px",
          background: "transparent",
          border: "none",
          color: open ? "var(--ql-yellow)" : "var(--ql-fg)",
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <Pencil size={15} strokeWidth={1.75} />
        {isRecovery ? "Recover this clip" : "Edit clip"}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ql-fg-muted)", fontWeight: 500 }}>
          {open
            ? "hide"
            : isRecovery
            ? (canReuse ? "retry, restore, or re-cut" : "set a new start to re-run")
            : "captions, sound, camera, or a fresh cut"}
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 18px 18px" }}>
          {errorRow}

          {isRecovery && (
            <div
              style={{
                padding: 16,
                marginBottom: 14,
                borderRadius: 14,
                background: "rgba(250,204,21,.06)",
                border: "1px solid rgba(250,204,21,.30)",
                color: "var(--ql-fg)",
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: canReuse || restorableIdx !== null ? 14 : 0 }}>
                {status === "failed"
                  ? "This job failed before producing a video."
                  : status === "incomplete"
                  ? "This job is incomplete — likely interrupted by a server restart."
                  : "This clip has no video file right now."}
                {canReuse
                  ? " Your original settings (start, bounds, fine-pan and music) were saved — recover in one click, no re-entry."
                  : " Original settings weren't saved for this job; set a new start below to re-run."}
              </div>

              {(canReuse || restorableIdx !== null) && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canReuse && (
                    <button
                      onClick={() => { void handleRetryReuse(); }}
                      disabled={busy}
                      title="Re-run the pipeline with the exact parameters this clip was created with — start, end/duration, phrases, fine-pan and music. No re-entry."
                      style={{
                        ...ctaSmallStyle,
                        cursor: busy ? "not-allowed" : "pointer",
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? <Loader2 size={14} className="ql-spin" /> : <RotateCcw size={14} strokeWidth={2} />}
                      Retry (reuse settings)
                    </button>
                  )}
                  {restorableIdx !== null && (
                    <button
                      onClick={() => { void handleRestoreLatest(); }}
                      disabled={busy}
                      title="Bring back the last finished render for this clip instantly — no re-render."
                      style={{
                        ...ctaGhostStyle,
                        cursor: busy ? "not-allowed" : "pointer",
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      <Undo2 size={14} strokeWidth={2} />
                      Restore previous render
                    </button>
                  )}
                </div>
              )}

              {canReuse && (
                <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 10 }}>
                  Need a different start or trim? Use Re-cut below.
                </div>
              )}
            </div>
          )}

          {hasVideo && (
            <EditSectionRow
              label="Captions & text"
              costHint="~30s – 2 min"
              tint="rgba(110, 231, 183, .035)"
              defaultOpen={false}
            >
              {captionsCards}
            </EditSectionRow>
          )}

          {hasVideo && (
            <EditSectionRow
              label="Sound"
              costHint="~10–20s — no re-render"
              tint="rgba(147, 197, 253, .035)"
              defaultOpen={false}
            >
              {soundCards}
            </EditSectionRow>
          )}

          {hasVideo && canReuse && (
            <EditSectionRow
              label="Camera & framing"
              costHint="~2–6 min"
              tint="rgba(196, 181, 253, .035)"
              defaultOpen={false}
            >
              {cameraCards}
            </EditSectionRow>
          )}

          <EditSectionRow
            label="Re-cut from scratch"
            costHint="full pipeline · ~5–10 min"
            tint="rgba(251, 146, 60, .03)"
            defaultOpen={isRecovery}
          >
            {recutCards}
          </EditSectionRow>
        </div>
      )}
    </div>
  );
}

/**
 * End/Duration segmented toggle for the Re-cut card. Same look and
 * behaviour as the one on the New Job screen — kept local so we don't
 * pull NewJob.tsx as a dependency just for this pill.
 */
function ReclipEndModeToggle({
  mode,
  onChange,
}: {
  mode: EndMode;
  onChange: (m: EndMode) => void;
}): React.JSX.Element {
  const opts: Array<{ id: EndMode; label: string }> = [
    { id: "end", label: "End" },
    { id: "duration", label: "Duration" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 2,
        borderRadius: 999,
        background: "rgba(255,255,255,.04)",
        border: "1px solid rgba(255,255,255,.06)",
        textTransform: "none",
        letterSpacing: "normal",
      }}
    >
      {opts.map((o) => {
        const active = mode === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              padding: "3px 11px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "inherit",
              border: "none",
              cursor: "pointer",
              background: active ? "linear-gradient(135deg,#FFD500,#FFA500)" : "transparent",
              color: active ? "#000" : "var(--ql-fg-muted)",
              transition: "all .15s var(--ql-ease)",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
